import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useToast } from '../../../Hooks/ToasContext';
import { ICustomerEntity } from '../../../Services/api/customers/customers.interface';
import { ListCustomersUseCase } from '../../../Usecase/Customers/list-customers-usecase';
import api from '../../../Services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { IAddressEntity } from '../../../Services/api/addresses/addresses.interface';
import { IProductEntity } from '../../../Services/api/products/products.interface';
import { ProductsApiRemoteService } from '../../../Services/api/products/products-service';
import { ListProductUseCase } from '../../../Usecase/Products/list-product-usecase';

export interface EditableManualOrder {
  id: string;
  number: string;
  omieCompanyId: string;
  omieCompany?: { id: string; name: string; alias?: string };
  store: 'B2B' | 'B2C';
  customerId: string;
  customer: ICustomerEntity;
  addressId: string;
  address?: IAddressEntity;
  carrierId?: string;
  carrier?: { id: string; name: string };
  shippingCost?: string;
  hasShippingCost: boolean;
  sellerId?: string;
  seller?: { id: string; name: string };
  discountValue: string;
  observations?: string;
  finalConsumer?: boolean;
  customerPaymentPreferenceId?: string;
  paymentType: 'PIX' | 'TICKET' | 'CREDIT_CARD';
  installmentsInDays: number[];
  omieIntegrationStatus: string;
  omieIntegrationDetails?: unknown[];
  stockMovements?: unknown[];
  stockProcessedAt?: string;
  items: Array<{
    id: string;
    productId: string;
    productMultiVariantId?: string;
    quantity: number;
    unitPrice: string;
    product: IProductEntity;
  }>;
}

export const canEditManualOrder = (order: { omieIntegrationStatus?: string }) =>
  order.omieIntegrationStatus !== 'PROCESSING' && order.omieIntegrationStatus !== 'SUCCESS';

const centsToDecimal = (value?: string) => (Number(value ?? 0) / 100).toFixed(2);

interface ManualOrderFormData {
  omieCompanyId: string;
  store: '' | 'B2B' | 'B2C';
  customerId: string;
  finalConsumer: boolean;
  sellerId: string;
  observations: string;
}

interface OmieCompany {
  id: string;
  name: string;
  alias?: string;
  active: boolean;
  canIssueInvoice: boolean;
}

export interface ManualOrderProductData {
  productId: string;
  productMultiVariantId?: string;
  quantity: number;
  unitPrice: string;
}

interface ManualOrderSelectionData {
  editFields?: {
    hasShippingCost: boolean;
    customerPaymentPreferenceId: string | null;
  };
  addressId: string;
  products: ManualOrderProductData[];
  carrierId: string;
  shippingCost: string;
  paymentPreference: {
    type: 'PIX' | 'TICKET' | 'CREDIT_CARD';
    installmentsInDays: number[];
  };
}

interface UseCreateManualOrderProps {
  listCustomersUseCase: ListCustomersUseCase;
  initialOrder?: EditableManualOrder;
}

function useManualOrderFields({
  listCustomersUseCase,
  initialOrder,
}: UseCreateManualOrderProps) {
  const [loading, setLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<ICustomerEntity[]>([]);
  const [companyOptions, setCompanyOptions] = useState<Array<{ label: string; value: string }>>(
    initialOrder ? [{ value: initialOrder.omieCompanyId, label: initialOrder.omieCompany?.alias || initialOrder.omieCompany?.name || 'Empresa do pedido' }] : []
  );
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<ICustomerEntity | undefined>(initialOrder?.customer);

  const { showToast } = useToast();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setValue,
  } = useForm<ManualOrderFormData>({
    defaultValues: {
      omieCompanyId: initialOrder?.omieCompanyId ?? '',
      store: initialOrder?.store ?? '',
      customerId: initialOrder?.customerId ?? '',
      finalConsumer: initialOrder?.finalConsumer ?? false,
      sellerId: initialOrder?.sellerId ?? initialOrder?.seller?.id ?? '',
      observations: initialOrder?.observations ?? '',
    },
  });

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setCompaniesLoading(true);
        const response = await api.get('/backoffice/omie-companies');
        const responseData = response.data;
        const companies: OmieCompany[] = Array.isArray(responseData)
          ? responseData
          : responseData?.data ?? responseData?.items ?? responseData?.results ?? [];

        const availableCompanies = companies
          .filter((company) => company.active && company.canIssueInvoice)
          .map((company) => ({
            label: company.alias
              ? `${company.name} (${company.alias})`
              : company.name,
            value: company.id,
          }));
        if (initialOrder?.omieCompany && !availableCompanies.some((company) => company.value === initialOrder.omieCompanyId)) {
          availableCompanies.push({
            value: initialOrder.omieCompanyId,
            label: initialOrder.omieCompany.alias || initialOrder.omieCompany.name,
          });
        }
        setCompanyOptions(availableCompanies);
      } catch {
        setCompanyOptions([]);
        showToast('Não foi possível carregar as empresas.', 'error');
      } finally {
        setCompaniesLoading(false);
      }
    };

    void loadCompanies();
  }, [showToast, initialOrder]);

  useEffect(() => {
    // Evita pesquisar enquanto o usuário digitou muito pouco.
    if (customerSearch.trim().length < 2) {
      setCustomers([]);
      return;
    }

    const timeout = window.setTimeout(async () => {
      try {
        setLoading(true);

        const result = await listCustomersUseCase.handle({
          page: 1,
          searchBy: customerSearch.trim(),
        });

        if (result.isFailure) {
          showToast('Não foi possível pesquisar os clientes.', 'error');
          return;
        }

        setCustomers(result.getValue()?.data ?? []);
      } catch {
        showToast('Não foi possível pesquisar os clientes.', 'error');
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => window.clearTimeout(timeout);
  }, [customerSearch, listCustomersUseCase, showToast]);

  const handleSelectCustomer = async (customer: ICustomerEntity) => {
    try {
      setLoading(true);
      const response = await api.get<ICustomerEntity>(`/customers/${customer.id}`);
      const detailedCustomer = response.data;

      setSelectedCustomer(detailedCustomer);
      setValue('customerId', detailedCustomer.id, {
        shouldValidate: true,
        shouldDirty: true,
      });

      return detailedCustomer;
    } catch {
      showToast('Não foi possível carregar os dados completos do cliente.', 'error');
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (
    values: ManualOrderFormData,
    selection: ManualOrderSelectionData,
  ) => {
    if (!values.omieCompanyId) {
      showToast('Selecione a empresa.', 'error');
      return;
    }

    if (!values.store) {
      showToast('Selecione a loja.', 'error');
      return;
    }

    if (!selectedCustomer) {
      showToast('Selecione um cliente.', 'error');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        omieCompanyId: values.omieCompanyId,
        store: values.store,
        customerId: selectedCustomer.id,
        finalConsumer: values.finalConsumer,
        sellerId: values.sellerId || null,
        observations: values.observations,
        addressId: selection.addressId,
        items: selection.products,
        carrierId: initialOrder ? selection.carrierId || null : selection.carrierId,
        shippingCost: selection.shippingCost,
        paymentType: selection.paymentPreference.type,
        installmentsInDays: selection.paymentPreference.installmentsInDays,
        ...(initialOrder ? selection.editFields : {}),
      };

      if (initialOrder) {
        await api.patch(`/manual-orders/${initialOrder.id}`, payload);
      } else {
        await api.post('/manual-orders', payload);
      }

      showToast(initialOrder ? 'Pedido manual atualizado com sucesso.' : 'Pedido manual criado com sucesso.', 'success');
      navigate('/manual-orders-list');
    } catch (error: any) {
      showToast(error?.response?.data?.message || (initialOrder ? 'Não foi possível atualizar o pedido manual.' : 'Não foi possível criar o pedido manual.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return {
    control,
    handleSubmit,
    handleAction,
    loading,
    companiesLoading,
    companyOptions,
    customers,
    customerSearch,
    setCustomerSearch,
    selectedCustomer,
    handleSelectCustomer,
  };
}

const getAddressKey = (address: IAddressEntity, index: number) =>
  address.id || String(index);

interface AddedProduct extends IProductEntity {
  selectionKey: string;
  productMultiVariantId: string;
  quantity: string;
  unitPrice: string;
}

interface Carrier {
  id: string;
  name: string;
  isActive: boolean;
}

interface CarrierCompanyLink {
  omieCompanyId: string;
  active: boolean;
}

const DEFAULT_CARRIER_OPTIONS = [
  {
    label: 'Frota Interna',
    value: '638e2101-bef2-46f0-8e00-8a43f484db65',
  },
  {
    label: 'Retirada na Loja',
    value: '7cd87da6-43ab-454f-b8b0-2899f6379415',
  },
];

const normalizeRows = <T,>(responseData: unknown): T[] => {
  if (Array.isArray(responseData)) return responseData as T[];

  const data = responseData as {
    data?: T[];
    items?: T[];
    results?: T[];
  } | undefined;

  return data?.data ?? data?.items ?? data?.results ?? [];
};

type ManualPaymentType = '' | 'PIX' | 'TICKET' | 'CREDIT_CARD';

export function useCreateManualOrder({ listCustomersUseCase, initialOrder }: UseCreateManualOrderProps) {
  const [addedCustomer, setAddedCustomer] = useState<ICustomerEntity | undefined>(initialOrder?.customer);
  const [selectedAddressKey, setSelectedAddressKey] = useState(initialOrder?.addressId ?? '');
  const [addedProducts, setAddedProducts] = useState<AddedProduct[]>(() => initialOrder?.items.map((item) => ({
    ...item.product,
    selectionKey: item.id,
    productMultiVariantId: item.productMultiVariantId ?? '',
    quantity: String(item.quantity),
    unitPrice: centsToDecimal(item.unitPrice),
  })) ?? []);
  const [showCustomersModal, setShowCustomersModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [manualPaymentType, setManualPaymentType] = useState<ManualPaymentType>(initialOrder?.paymentType ?? '');
  const [manualTicketDays, setManualTicketDays] = useState<string[]>(initialOrder?.installmentsInDays.map(String) ?? []);
  const [ignoreCustomerPayment, setIgnoreCustomerPayment] = useState(false);
  const [carrierOptions, setCarrierOptions] = useState<Array<{ label: string; value: string }>>(
    initialOrder?.carrier ? [{ label: initialOrder.carrier.name, value: initialOrder.carrier.id }] : []
  );
  const [selectedCarrierId, setSelectedCarrierId] = useState(initialOrder?.carrierId ?? '');
  const [freightValue, setFreightValue] = useState(centsToDecimal(initialOrder?.shippingCost));
  const [carriersLoading, setCarriersLoading] = useState(false);
  const [productDetailsLoading, setProductDetailsLoading] = useState(false);
  const [paymentPreferenceId, setPaymentPreferenceId] = useState(initialOrder?.customerPaymentPreferenceId ?? '');
  const previousCompanyId = useRef(initialOrder?.omieCompanyId ?? '');
  const navigate = useNavigate();
  const { showToast } = useToast();
  const listProductsUseCase = useMemo(
    () => new ListProductUseCase(new ProductsApiRemoteService(api)),
    []
  );

  const {
    control,
    handleSubmit,
    handleAction,
    loading,
    companiesLoading,
    companyOptions,
    handleSelectCustomer,
  } = useManualOrderFields({
    listCustomersUseCase,
    initialOrder,
  });
  const selectedCompanyId = useWatch({ control, name: 'omieCompanyId' });
  const selectedStore = useWatch({ control, name: 'store' });

  useEffect(() => {
    let active = true;

    if (previousCompanyId.current !== selectedCompanyId) {
      setSelectedCarrierId('');
      setFreightValue('0.00');
    }
    previousCompanyId.current = selectedCompanyId;
    const initialCarrierOptions = initialOrder?.carrier && selectedCompanyId === initialOrder.omieCompanyId
      ? [...DEFAULT_CARRIER_OPTIONS, { label: initialOrder.carrier.name, value: initialOrder.carrier.id }]
      : DEFAULT_CARRIER_OPTIONS;
    setCarrierOptions(Array.from(new Map(initialCarrierOptions.map((carrier) => [carrier.value, carrier])).values()));

    if (!selectedCompanyId) return () => { active = false; };

    const loadCompanyCarriers = async () => {
      try {
        setCarriersLoading(true);
        const carriersResponse = await api.get('/carriers', {
          params: { page: 1, offset: 100 },
        });
        const carriers = normalizeRows<Carrier>(carriersResponse.data)
          .filter((carrier) => carrier.isActive);

        const relatedCarriers = await Promise.all(carriers.map(async (carrier) => {
          const linksResponse = await api.get(
            `/backoffice/carriers/${carrier.id}/omie-companies`
          );
          const links = normalizeRows<CarrierCompanyLink>(linksResponse.data);
          const isRelated = links.some((link) =>
            link.active && link.omieCompanyId === selectedCompanyId
          );

          return isRelated ? carrier : undefined;
        }));

        if (active) {
          const companyCarrierOptions = relatedCarriers
            .filter((carrier): carrier is Carrier => Boolean(carrier))
            .map((carrier) => ({ label: carrier.name, value: carrier.id }));
          const optionsById = new Map(
            [...initialCarrierOptions, ...companyCarrierOptions]
              .map((carrier) => [carrier.value, carrier])
          );

          setCarrierOptions(Array.from(optionsById.values()));
        }
      } catch {
        if (active) showToast('Não foi possível carregar as transportadoras.', 'error');
      } finally {
        if (active) setCarriersLoading(false);
      }
    };

    void loadCompanyCarriers();

    return () => { active = false; };
  }, [selectedCompanyId, initialOrder, showToast]);

  const handleAddCustomer = async (customer: ICustomerEntity) => {
    const detailedCustomer = await handleSelectCustomer(customer);
    if (!detailedCustomer) return;

    setAddedCustomer(detailedCustomer);
    setPaymentPreferenceId('');
    setSelectedAddressKey(
      detailedCustomer.addresses?.length === 1
        ? getAddressKey(detailedCustomer.addresses[0], 0)
        : ''
    );
    setManualPaymentType('');
    setManualTicketDays([]);
    setIgnoreCustomerPayment(false);
    setShowCustomersModal(false);
  };

  const handleRemoveCustomer = () => {
    setAddedCustomer(undefined);
    setPaymentPreferenceId('');
    setSelectedAddressKey('');
    setAddedProducts([]);
    setShowProductsModal(false);
    setShowCustomersModal(false);
    setManualPaymentType('');
    setManualTicketDays([]);
    setIgnoreCustomerPayment(false);
  };

  const handleFormSubmit = (values: Parameters<typeof handleAction>[0]) => {
    if (!paymentType || !addedCustomer || !selectedAddress?.id || !hasValidProducts || !hasValidFreight || !hasValidPayment) return;

    void handleAction(values, {
      addressId: selectedAddress.id,
      products: addedProducts.map((product) => ({
        productId: product.id,
        productMultiVariantId: product.productMultiVariantId || undefined,
        quantity: Number(product.quantity),
        unitPrice: String(Math.round(Number(product.unitPrice) * 100)),
      })),
      paymentPreference: {
        type: paymentType,
        installmentsInDays: paymentType === 'PIX' && !initialOrder ? [0] : paymentDays,
      },
      ...(initialOrder ? {
        editFields: {
          hasShippingCost: Number(freightValue) > 0,
          customerPaymentPreferenceId: paymentPreferenceId || null,
        },
      } : {}),
      carrierId: selectedCarrierId,
      shippingCost: String(Math.round(Number(freightValue) * 100)),
    });
  };

  const handleRemoveAddress = () => {
    setSelectedAddressKey('');
    setAddedProducts([]);
    setShowProductsModal(false);
  };

  const handleAddProduct = async (product: IProductEntity) => {
    if (addedProducts.some((item) => item.id === product.id)) {
      setShowProductsModal(false);
      return;
    }

    let productToAdd = product;

    try {
      setProductDetailsLoading(true);
      const response = await api.get(`/products/${product.id}`);
      productToAdd = response.data?.data ?? response.data;
    } catch {
      if (product.isMultiVariants) {
        showToast('Não foi possível carregar as variações do produto.', 'error');
        return;
      }
    } finally {
      setProductDetailsLoading(false);
    }

    setAddedProducts((current) => {
      if (current.some((item) => item.id === productToAdd.id)) return current;

      return [
        ...current,
        {
          ...productToAdd,
          selectionKey: productToAdd.id,
          productMultiVariantId: '',
          quantity: '1',
          unitPrice: productToAdd.price || '0',
        },
      ];
    });
    setShowProductsModal(false);
  };

  const handleUpdateProductVariant = (productId: string, variantId: string) => {
    setAddedProducts((current) => current.map((product) =>
      product.selectionKey === productId
        ? { ...product, productMultiVariantId: variantId }
        : product
    ));
  };

  const handleUpdateProduct = (
    productId: string,
    field: 'quantity' | 'unitPrice',
    value: string
  ) => {
    setAddedProducts((current) => current.map((product) =>
      product.selectionKey === productId
        ? { ...product, [field]: value }
        : product
    ));
  };

  const handleRemoveProduct = (productId: string) => {
    setAddedProducts((current) => current.filter((product) => product.selectionKey !== productId));
  };

  const handleRemoveCustomerPayment = () => {
    setIgnoreCustomerPayment(true);
    setPaymentPreferenceId('');
    setManualPaymentType('');
    setManualTicketDays([]);
  };

  const handleAddTicketDay = () => {
    setManualTicketDays((current) => [...current, '']);
  };

  const handleUpdateTicketDay = (index: number, value: string) => {
    setManualTicketDays((current) => current.map((day, currentIndex) =>
      currentIndex === index ? value : day
    ));
  };

  const handleRemoveTicketDay = (index: number) => {
    setManualTicketDays((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const customerAddresses = addedCustomer?.addresses ?? [];
  const selectedAddress = customerAddresses.find(
    (address, index) => getAddressKey(address, index) === selectedAddressKey
  );
  const addressRows = customerAddresses.map((address, index) => ({
    ...address,
    id: getAddressKey(address, index),
  }));
  const hasValidProducts = addedProducts.length > 0 && addedProducts.every(
    (product) => product.quantity.trim() !== ''
      && product.unitPrice.trim() !== ''
      && Number(product.quantity) > 0
      && (!initialOrder || Number.isInteger(Number(product.quantity)))
      && (initialOrder ? Number(product.unitPrice) > 0 : Number(product.unitPrice) >= 0)
      && (!product.isMultiVariants || Boolean(product.productMultiVariantId))
  );
  const productsTotal = addedProducts.reduce(
    (total, product) => total + (Number(product.quantity) * Number(product.unitPrice)),
    0
  );
  const hasValidFreight = (Boolean(initialOrder) || Boolean(selectedCarrierId))
    && freightValue.trim() !== ''
    && Number(freightValue) >= 0;
  const orderTotal = productsTotal + (Number(freightValue) || 0) - Number(centsToDecimal(initialOrder?.discountValue));
  const customerPaymentPreference = initialOrder
    ? addedCustomer?.paymentPreferences?.find((preference) => preference.id === paymentPreferenceId)
    : ignoreCustomerPayment ? undefined : addedCustomer?.paymentPreferences?.[0];
  const paymentType = customerPaymentPreference?.type ?? manualPaymentType;
  const paymentDays = customerPaymentPreference?.installmentsInDays
    ?? (initialOrder || manualPaymentType === 'TICKET'
      ? manualTicketDays.map(Number)
      : []);
  const hasValidPayment = initialOrder
    ? Boolean(paymentType) && paymentDays.length > 0
      && paymentDays.every((day, index) => (customerPaymentPreference || manualTicketDays[index]?.trim() !== '') && Number.isInteger(day) && day >= 0)
    : paymentType === 'PIX'
    || (paymentType === 'TICKET'
      && paymentDays.length > 0
      && paymentDays.every((day) => Number.isInteger(day) && day > 0));

  const addressColumns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Endereço',
      flex: 1,
      minWidth: 220,
      valueGetter: (_value: unknown, row: IAddressEntity) =>
        `${row.name}, ${row.number}`,
    },
    {
      field: 'locality',
      headerName: 'Bairro',
      flex: 0.7,
      minWidth: 140,
    },
    {
      field: 'city',
      headerName: 'Cidade/UF',
      flex: 0.7,
      minWidth: 150,
      valueGetter: (_value: unknown, row: IAddressEntity) =>
        `${row.city} - ${row.province}`,
    },
    {
      field: 'zipcode',
      headerName: 'CEP',
      width: 120,
    },
  ], []);

  return {
    control,
    handleSubmit,
    handleFormSubmit,
    loading,
    companiesLoading,
    companyOptions,
    selectedCompanyId,
    selectedStore,
    addedCustomer,
    selectedAddress,
    customerAddresses,
    addressRows,
    addressColumns,
    addedProducts,
    hasValidProducts,
    hasValidFreight,
    hasValidPayment,
    carriersLoading,
    carrierOptions,
    selectedCarrierId,
    setSelectedCarrierId,
    freightValue,
    setFreightValue,
    orderTotal,
    customerPaymentPreference,
    paymentDays,
    manualPaymentType,
    setManualPaymentType,
    setManualTicketDays,
    manualTicketDays,
    showCustomersModal,
    setShowCustomersModal,
    showProductsModal,
    setShowProductsModal,
    productDetailsLoading,
    handleAddCustomer,
    handleAddProduct,
    handleRemoveCustomer,
    handleRemoveAddress,
    handleUpdateProductVariant,
    handleUpdateProduct,
    handleRemoveProduct,
    handleRemoveCustomerPayment,
    handleUpdateTicketDay,
    handleRemoveTicketDay,
    handleAddTicketDay,
    setSelectedAddressKey,
    navigate,
    listProductsUseCase
  };
}

export function useManualOrderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<EditableManualOrder>();
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setOrder(undefined);
    setError('');
    if (!id) return () => { active = false; };
    const load = async () => {
      try {
        const response = await api.get(`/manual-orders/${id}`);
        const data: EditableManualOrder = response.data?.data ?? response.data;
        if (!canEditManualOrder(data)) {
          throw new Error('Não é possível editar um pedido em processamento ou já integrado ao Omie.');
        }
        const [customerResponse, products] = await Promise.all([
          api.get(`/customers/${data.customerId}`),
          Promise.all(data.items.map(async (item) => {
            const productResponse = await api.get(`/products/${item.productId}`);
            return { ...item, product: productResponse.data?.data ?? productResponse.data };
          })),
        ]);
        const customer = customerResponse.data?.data ?? customerResponse.data;
        if (data.address && !customer.addresses?.some((address: { id: string }) => address.id === data.addressId)) {
          customer.addresses = [...(customer.addresses ?? []), data.address];
        }
        if (active) setOrder({ ...data, customer, items: products });
      } catch (error: any) {
        if (active) setError(error?.response?.data?.message || error?.message || 'Não foi possível carregar o pedido manual.');
      }
    };
    void load();
    return () => { active = false; };
  }, [id, attempt]);

  const handleRetry = () => setAttempt((value) => value + 1);
  return { id, order, error, navigate, handleRetry };
}
